
import { Component, Vue, Prop } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import { DeviceService } from "@sureview/camera";
import { DeviceControllerOutput } from "@/store/site-monitor-cameras/types";
import { EventDetails } from "@/store/site-monitor/types";
import { SessionResource } from "@/store/sessions/types";
import { renderDistance } from "@/filters";
import api from "@/services/api.service";

const SiteMonitor = namespace("siteMonitor");
const Session = namespace("sessions");

@Component({
	filters: {
		renderDistance
	}
})
export default class ControlDeviceItem extends Vue {
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@SiteMonitor.Getter("getEventDetails") getEventDetails: EventDetails;
	@SiteMonitor.Mutation setActivity: () => void;

	@Session.Getter getSession: (resourceId: number) => any;

	@Prop({ required: true }) output!: DeviceControllerOutput;

	@Prop({ type: Boolean, default: true }) showTitle!: boolean;

	// When false the control is always shown (no expand/collapse, no Cancel). Used in fixed containers like a pop-over.
	@Prop({ type: Boolean, default: true }) collapsible!: boolean;

	// Success confirmation shown after a command is sent. Cleared when the control is reopened
	// (collapse then expand, or leaving and returning to the tab) so a stale tick is never shown.
	private commandStatus: string = "";

	public get outputReasonOptional(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "RelayControl", "ReasonOptional"]);
	}

	// Whether the device exposes any control action (pulse or on/off).
	public get hasActions(): boolean {
		return this.output.canPulse || this.output.canOnOff;
	}

	private get hasActiveEvent(): boolean {
		return !!this.getEventDetails && this.getEventDetails.eventID > 0;
	}

	// Always expanded when not collapsible.
	public get isExpanded(): boolean {
		return !this.collapsible || this.output.controlRequest;
	}

	public pulseOutput(): void {
		this.output.controlRequest = true;
		this.commandStatus = "";

		this.$nextTick(() => {
			const refKey = `reason-${this.output.objectId}`;
			let textArea = this.$refs[refKey];

			if (!textArea) {
				return;
			}

			if (Array.isArray(textArea)) {
				textArea = textArea[0];

				if (!textArea) {
					return;
				}
			}

			if (textArea instanceof HTMLTextAreaElement) {
				textArea.focus();
			}
		});
	}

	private cancelPulse(): void {
		this.output.controlRequest = false;
		this.output.controlReason = "";
		this.commandStatus = "";
		this.output.controlError = "";
	}

	public async doPulseOutput(): Promise<void> {
		if ((!this.outputReasonOptional && (!this.output.controlReason || !this.output.controlReason.trim().length)) || this.output.sendingCommand) {
			return;
		}

		if (!this.hasActiveEvent) {
			this.output.controlError = "No active event to record this action against.";
			return;
		}

		this.commandStatus = "";
		this.output.controlError = "";
		try
		{
			this.setActivity();
			this.output.sendingCommand = true;

			const device = await api.deviceAction(
				this.output.objectId,
				{
					eventId: this.getEventDetails.eventID,
					deviceAction: "pulse",
					outputReason: this.output.controlReason
				}
			);

			const auth = this.getSession(SessionResource.DeviceServiceSession);
			const deviceService = new DeviceService(api.getDeviceServiceAddress(), device.deviceServerEndpoint);

			await deviceService.sendOutputCmd(auth, this.output.objectId, "output", "pulse").then(
				() => {
					this.output.controlRequest = false;
					this.output.controlReason = "";
					this.output.sendingCommand = false;
					this.commandStatus = this.output.isOutput ? "Pulse actioned" : "Momentary Unlock actioned";
					this.$notify({ type: "success", title: this.commandStatus, text: this.output.title });
				},
				error => {
					console.error(error);
					this.output.controlError = error && error.statusText ? error.statusText : "Error triggering relay";
					this.output.sendingCommand = false;
					this.$notify({ type: "error", title: "Failed to trigger relay", text: this.output.controlError });
				}
			);
		}
		catch(error)
		{
			console.error(error);
			this.output.controlError = error && error.statusText ? error.statusText : "Error triggering relay";
			this.output.sendingCommand = false;
			this.$notify({ type: "error", title: "Failed to trigger relay", text: this.output.controlError });
			return;
		}

	}

	public async setOutput(on: boolean): Promise<void> {
		if (!this.hasActiveEvent) {
			this.output.controlError = "No active event to record this action against.";
			return;
		}

		this.commandStatus = "";
		this.output.controlError = "";

		const action = on ? "on" : "off";

		try {
			this.output.sendingCommand = true;

			const device = await api.deviceAction(
				this.output.objectId,
				{
					eventId: this.getEventDetails.eventID,
					deviceAction: action,
					outputReason: this.output.controlReason
				}
			);

			const auth = this.getSession(SessionResource.DeviceServiceSession);
			const deviceService = new DeviceService(api.getDeviceServiceAddress(), device.deviceServerEndpoint);

			await deviceService.sendOutputCmd(auth, this.output.objectId, "output", action).then(
				() => {
					this.output.controlRequest = false;
					this.output.controlReason = "";
					this.output.sendingCommand = false;
					this.commandStatus = on ? "On actioned" : "Off actioned";
					this.$notify({ type: "success", title: this.commandStatus, text: this.output.title });
				},
				error => {
					console.error(error);
					this.output.controlError = error && error.statusText ? error.statusText : "Error triggering relay";
					this.output.sendingCommand = false;
					this.$notify({ type: "error", title: "Failed to trigger relay", text: this.output.controlError });
				}
			);
		}
		catch (error) {
			console.error(error);
			this.output.controlError = error && error.statusText ? error.statusText : "Error triggering relay";
			this.output.sendingCommand = false;
			this.$notify({ type: "error", title: "Failed to trigger relay", text: this.output.controlError });
		}
	}

	public isOutputDisabled(output: DeviceControllerOutput): boolean {
		if (output.sendingCommand) {
			return true;
		}

		return !this.outputReasonOptional && (!output.controlReason || !output.controlReason.trim().length);
	}
}
